import {
  motion,
  useMotionValue,
  useMotionTemplate,
  useTransform,
  useSpring,
  useReducedMotion,
} from "framer-motion";
import {
  FaMapMarkerAlt,
  FaBolt,
  FaLayerGroup,
  FaHeadset,
  FaNetworkWired,
  FaFingerprint,
} from "react-icons/fa";
import Eyebrow from "../common/Eyebrow";
import CountUp from "../common/CountUp";
import { ALL_DATA_CENTERS } from "../../constant/AllDataCenters";

const RACK_UNITS = [
  { width: 92, leds: 3 },
  { width: 78, leds: 2 },
  { width: 100, leds: 4 },
  { width: 85, leds: 2 },
  { width: 95, leds: 3 },
  { width: 70, leds: 2 },
  { width: 100, leds: 3 },
];

const TOTAL_LOCATIONS = Object.keys(ALL_DATA_CENTERS).length;

const STATS = [
  {
    icon: FaMapMarkerAlt,
    value: TOTAL_LOCATIONS,
    suffix: "+",
    label: "Locations, India & international",
  },
  { icon: FaBolt, value: 99.99, decimals: 2, suffix: "%", label: "Uptime SLA" },
  { icon: FaLayerGroup, value: null, display: "Tier-3", label: "Facility design" },
  { icon: FaHeadset, value: null, display: "24/7", label: "NOC & support" },
];

// Facility-attribute variant used on the home page, where the uptime, location
// count, and 24/7 figures are already shown in the stats strip above the fold.
export const FACILITY_STATS = [
  {
    icon: FaBolt,
    value: null,
    display: "N+1",
    label: "Power redundancy, UPS-backed",
  },
  {
    icon: FaNetworkWired,
    value: null,
    display: "Multi-ISP",
    label: "Carrier-neutral peering",
  },
  { icon: FaLayerGroup, value: null, display: "Tier-3", label: "Facility design" },
  {
    icon: FaFingerprint,
    value: null,
    display: "Biometric",
    label: "Access control & CCTV",
  },
];

const TiltRack = () => {
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);

  const rotateX = useSpring(useTransform(py, [0, 1], [12, -12]), {
    stiffness: 150,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(px, [0, 1], [-12, 12]), {
    stiffness: 150,
    damping: 20,
  });
  const spotlight = useMotionTemplate`radial-gradient(360px circle at ${useTransform(
    px,
    [0, 1],
    ["0%", "100%"]
  )} ${useTransform(py, [0, 1], ["0%", "100%"])}, rgba(var(--website-secondary-rgb),0.25), transparent 70%)`;

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - rect.left) / rect.width);
    py.set((e.clientY - rect.top) / rect.height);
  };
  const handleMouseLeave = () => {
    px.set(0.5);
    py.set(0.5);
  };

  return (
    <div style={{ perspective: 1400 }} className="mx-auto w-full max-w-sm">
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="group relative rounded-3xl border border-white/10 bg-[var(--surface-raised)] p-5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
      >
        <motion.div
          className="pointer-events-none absolute inset-0 z-10 rounded-3xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: spotlight }}
        />

        <motion.div
          initial={{ scale: 0, rotate: -12 }}
          whileInView={{ scale: 1, rotate: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.2 }}
          style={{ transform: "translateZ(50px)" }}
          className="absolute -right-3 -top-3 z-20 flex items-center gap-2 rounded-full bg-[var(--website-secondary-color)] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--on-secondary)] shadow-lg"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--on-secondary)] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--on-secondary)]" />
          </span>
          Live
        </motion.div>

        <div
          style={{ transform: "translateZ(30px)" }}
          className="space-y-2.5"
        >
          {RACK_UNITS.map((unit, i) => (
            <div
              key={i}
              className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-3 py-2.5"
            >
              <div
                className="h-1.5 rounded-full bg-white/15"
                style={{ width: `${unit.width}%` }}
              />
              <div className="ml-auto flex flex-shrink-0 items-center gap-1">
                {Array.from({ length: unit.leds }).map((_, j) => (
                  <span
                    key={j}
                    className="h-1.5 w-1.5 rounded-full bg-[var(--website-secondary-color)]"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

const FloatingStat = ({ stat, index }) => {
  const Icon = stat.icon;
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="rounded-2xl border border-white/10 bg-white/[0.05] p-5 backdrop-blur-sm"
    >
      <motion.div
        animate={prefersReducedMotion ? undefined : { y: [0, -6, 0] }}
        transition={{
          duration: 3 + index * 0.4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: index * 0.3,
        }}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(var(--website-secondary-rgb),0.15)] text-[var(--website-secondary-color)]">
          <Icon size={16} />
        </div>
        <p className="mt-4 text-2xl font-bold text-white md:text-3xl">
          {stat.value !== null ? (
            <CountUp end={stat.value} decimals={stat.decimals || 0} suffix={stat.suffix} />
          ) : (
            stat.display
          )}
        </p>
        <p className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-400">
          {stat.label}
        </p>
      </motion.div>
    </motion.div>
  );
};

// `seamless` drops the section's own backdrop so it can sit inside a shared
// dark band (see the home page) without repeating the texture and glows.
const InfraShowcase3D = ({ stats = STATS, seamless = false }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      className={
        seamless
          ? "relative border-t border-white/[0.06] px-6 py-20"
          : "relative overflow-hidden bg-[var(--surface-dark)] px-6 py-24"
      }
    >
      {!seamless && (
        <>
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #ffffff 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
          <motion.div
            className="pointer-events-none absolute -left-24 top-10 h-80 w-80 rounded-full bg-[rgba(var(--website-secondary-rgb),0.2)] blur-3xl"
            animate={
              prefersReducedMotion ? undefined : { y: [0, 30, 0], x: [0, 20, 0] }
            }
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="pointer-events-none absolute -right-24 bottom-10 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl"
            animate={
              prefersReducedMotion
                ? undefined
                : { y: [0, -30, 0], x: [0, -20, 0] }
            }
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      )}

      <div className="relative mx-auto max-w-6xl">
        <div className="text-center">
          <Eyebrow className="text-[var(--website-secondary-color)]">
            Live infrastructure
          </Eyebrow>
          <h2 className="mx-auto mt-3 max-w-2xl text-3xl font-bold tracking-tight text-white md:text-4xl">
            Built like a network you can see
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-300">
            Every rack, link, and NOC shift behind QuantaRock is engineered
            for one outcome: infrastructure that just stays up. Tilt the rack
            to see what's inside.
          </p>
        </div>

        <div className="mt-14 grid items-center gap-10 lg:grid-cols-[1fr_1.1fr_1fr]">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
            <FloatingStat stat={stats[0]} index={0} />
            <FloatingStat stat={stats[1]} index={1} />
          </div>

          <TiltRack />

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
            <FloatingStat stat={stats[2]} index={2} />
            <FloatingStat stat={stats[3]} index={3} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default InfraShowcase3D;
